import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, TOKEN_KEY, ACT_AS_KEY, WORKSPACE_KEY, getActAs, getWorkspaceId } from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actAs, setActAsState] = useState(() => getActAs());
  const [workspaceId, setWorkspaceId] = useState(() => getWorkspaceId());
  const timerRef = useRef(null);
  const warnRef = useRef(null);

  const logout = useCallback(
    async (reason) => {
      const hadToken = !!localStorage.getItem(TOKEN_KEY);
      if (hadToken) {
        try {
          await api.post("/auth/logout", null, { skipActAs: true });
        } catch {
          /* ignorer */
        }
      }
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ACT_AS_KEY);
      localStorage.removeItem(WORKSPACE_KEY);
      setActAsState(null);
      setWorkspaceId(null);
      setUser(null);
      if (reason === "inactivity") {
        toast.info("Du ble logget ut automatisk på grunn av inaktivitet.");
      }
    },
    []
  );

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const res = await api.get("/auth/me", { skipActAs: true });
      setUser(res.data);
      // Fjern virksomhetsvalg som ikke lenger finnes (slettet / annen konto)
      const wsId = getWorkspaceId();
      if (wsId && !(res.data.workspaces || []).some((w) => w.id === wsId)) {
        localStorage.removeItem(WORKSPACE_KEY);
        setWorkspaceId(null);
      }
      // Fjern fullmakt-kontekst som ikke lenger er gyldig
      const current = getActAs();
      if (current) {
        const still = (res.data.delegations_received || []).find((d) => d.owner_id === current.owner_id);
        if (!still) {
          localStorage.removeItem(ACT_AS_KEY);
          setActAsState(null);
        } else if (still.role !== current.role) {
          const next = { ...current, role: still.role };
          localStorage.setItem(ACT_AS_KEY, JSON.stringify(next));
          setActAsState(next);
        }
      }
      return res.data;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ACT_AS_KEY);
      setActAsState(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
    const handler = () => {
      setUser(null);
      setActAsState(null);
    };
    window.addEventListener("rp:unauthorized", handler);
    return () => window.removeEventListener("rp:unauthorized", handler);
  }, [refreshUser]);

  // Auto-utlogging ved inaktivitet
  useEffect(() => {
    if (!user) return undefined;
    const minutes = Number(user.auto_logout_minutes || 15);
    const ms = minutes * 60 * 1000;
    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
      if (ms > 90 * 1000) {
        warnRef.current = setTimeout(() => {
          toast.warning("Du logges ut om ett minutt på grunn av inaktivitet. Beveg musen for å fortsette.", { id: "inactivity-warning", duration: 60000 });
        }, ms - 60 * 1000);
      }
      timerRef.current = setTimeout(() => logout("inactivity"), ms);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
    };
  }, [user, logout]);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password }, { skipActAs: true });
    localStorage.setItem(TOKEN_KEY, res.data.access_token);
    localStorage.removeItem(ACT_AS_KEY);
    localStorage.removeItem(WORKSPACE_KEY);
    setActAsState(null);
    setWorkspaceId(null);
    const me = await api.get("/auth/me", { skipActAs: true });
    setUser(me.data);
    return me.data;
  }, []);

  /** Innlogging med ferdig utstedt app-token (BankID-retur). */
  const loginWithToken = useCallback(async (token) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.removeItem(ACT_AS_KEY);
    localStorage.removeItem(WORKSPACE_KEY);
    setActAsState(null);
    setWorkspaceId(null);
    try {
      const me = await api.get("/auth/me", { skipActAs: true });
      setUser(me.data);
      return me.data;
    } catch (err) {
      localStorage.removeItem(TOKEN_KEY);
      throw err;
    }
  }, []);

  /** Registrering. Returnerer { me, result } der result er svaret fra /auth/register (bl.a. email_verification). */
  const register = useCallback(async (payload) => {
    const res = await api.post("/auth/register", payload, { skipActAs: true });
    localStorage.setItem(TOKEN_KEY, res.data.access_token);
    const me = await api.get("/auth/me", { skipActAs: true });
    setUser(me.data);
    return { me: me.data, result: res.data };
  }, []);

  /** Bytt kontekst til en eier man har fullmakt for (eller null = egen konto). Laster appen på nytt for ren tilstand. */
  const switchActAs = useCallback((delegation) => {
    if (delegation) {
      localStorage.setItem(ACT_AS_KEY, JSON.stringify({ owner_id: delegation.owner_id, owner_name: delegation.owner_name, role: delegation.role }));
    } else {
      localStorage.removeItem(ACT_AS_KEY);
    }
    window.location.assign("/");
  }, []);

  /** Bytt aktiv virksomhet. Laster appen på nytt for ren tilstand (hvelvet låses uansett ved reload). */
  const switchWorkspace = useCallback((id, home = "/") => {
    const primaryId = user?.id;
    if (!id || id === primaryId) localStorage.removeItem(WORKSPACE_KEY);
    else localStorage.setItem(WORKSPACE_KEY, id);
    localStorage.removeItem(ACT_AS_KEY);
    window.location.assign(home);
  }, [user?.id]);

  const workspaces = useMemo(() => user?.workspaces || [], [user?.workspaces]);
  const activeWorkspace = useMemo(
    () => workspaces.find((w) => w.id === (workspaceId || user?.id)) || workspaces.find((w) => w.is_primary) || null,
    [workspaces, workspaceId, user?.id]
  );

  const value = useMemo(() => ({
    user,
    loading,
    workspaces,
    activeWorkspace,
    switchWorkspace,
    login,
    loginWithToken,
    register,
    logout,
    refreshUser,
    setUser,
    actAs,
    switchActAs,
    isOwner: !actAs,
    canWrite: !actAs || actAs.role === "handtering",
  }), [user, loading, login, loginWithToken, register, logout, refreshUser, actAs, switchActAs, workspaces, activeWorkspace, switchWorkspace]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth må brukes innenfor AuthProvider");
  return ctx;
}
